import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Building2, Search, ChevronLeft, RefreshCw, Download,
  Activity, Send, ChevronRight, MoreHorizontal, PauseCircle, PlayCircle, Trash2, AlertTriangle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_COLOURS = {
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-orange-100 text-orange-800',
  deleted: 'bg-rose-100 text-rose-700',
  trial: 'bg-blue-100 text-blue-800',
  inactive: 'bg-gray-200 text-gray-700',
};

const HEALTH_COLOURS = {
  green: 'bg-green-100 text-green-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  red: 'bg-red-100 text-red-800',
};

export default function PlatformBuildings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());

  // Action dialogs
  const [statusDialog, setStatusDialog] = useState(null); // { action, hoaIds, names }
  const [statusReason, setStatusReason] = useState('');
  const [purgeDialog, setPurgeDialog] = useState(null);   // { hoa_id, name }
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purgeReason, setPurgeReason] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchBuildings = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: '300' });
    if (search) params.set('q', search);
    if (typeFilter !== 'all') params.set('building_type', typeFilter);
    if (countryFilter) params.set('country', countryFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    const r = await fetch(`${API}/api/platform-admin/buildings?${params}`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) {
      const d = await r.json();
      setBuildings(d.buildings || []);
    }
    setLoading(false);
  }, [search, typeFilter, countryFilter, statusFilter]);

  useEffect(() => {
    const t = setTimeout(fetchBuildings, 250);
    return () => clearTimeout(t);
  }, [fetchBuildings]);

  const toggleSelectAll = () => {
    if (selected.size === buildings.length) setSelected(new Set());
    else setSelected(new Set(buildings.map((b) => b.hoa_id)));
  };
  const toggleOne = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const exportEmails = async () => {
    const target = selected.size > 0 ? Array.from(selected) : buildings.map((b) => b.hoa_id);
    if (!target.length) return toast.error('No buildings to export');
    const r = await fetch(`${API}/api/platform-admin/buildings/bulk-action`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ hoa_ids: target, action: 'export_emails' }),
    });
    if (!r.ok) return toast.error('Export failed');
    const data = await r.json();
    const rows = data.rows || [];
    if (!rows.length) return toast.error('No emails found');
    const headers = ['Building', 'Name', 'Email', 'Phone', 'Role', 'Unit', 'Status'];
    const csv = [headers.join(',')].concat(
      rows.map((r) => [r.building_name, r.name, r.email, r.phone, r.role, r.unit, r.status]
        .map((v) => `"${(v ?? '').toString().replaceAll('"', '""')}"`).join(','))
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `bodycorp365-emails-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success(`Exported ${rows.length} emails`);
  };

  const recomputeHealth = async () => {
    const target = selected.size > 0 ? Array.from(selected) : buildings.map((b) => b.hoa_id);
    if (!target.length) return toast.error('No buildings selected');
    const r = await fetch(`${API}/api/platform-admin/buildings/bulk-action`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ hoa_ids: target, action: 'recompute_health' }),
    });
    if (r.ok) {
      toast.success('Health scores recomputed');
      fetchBuildings();
    } else toast.error('Recompute failed');
  };

  const messageSelected = () => {
    if (selected.size === 0) return toast.error('Select at least one building');
    navigate(`/platform/communications?hoas=${Array.from(selected).join(',')}`);
  };

  // ---------- Suspend / Activate / Soft-delete (archive) ----------
  const openStatusDialog = (action, hoaIds) => {
    if (!hoaIds.length) return toast.error('Select at least one building');
    const names = hoaIds.map((id) => buildings.find((b) => b.hoa_id === id)?.name || id);
    setStatusReason('');
    setStatusDialog({ action, hoaIds, names });
  };
  const runStatusAction = async () => {
    if (!statusDialog) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/platform-admin/buildings/bulk-action`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ hoa_ids: statusDialog.hoaIds, action: statusDialog.action, reason: statusReason || null }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Action failed');
      const d = await r.json();
      const ok = (d.results || []).filter((x) => x.ok).length;
      toast.success(`${ok} building(s) ${statusDialog.action === 'archive' ? 'deleted (soft)' : statusDialog.action + 'd'}`);
      setStatusDialog(null);
      setSelected(new Set());
      fetchBuildings();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  // ---------- Hard purge (irreversible, single building) ----------
  const runPurge = async () => {
    if (!purgeDialog) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/platform-admin/buildings/${purgeDialog.hoa_id}/purge`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ confirm: purgeConfirm, reason: purgeReason || null }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Purge failed');
      toast.success(`${purgeDialog.name} permanently deleted`);
      setPurgeDialog(null); setPurgeConfirm(''); setPurgeReason('');
      setSelected((s) => { const n = new Set(s); n.delete(purgeDialog.hoa_id); return n; });
      fetchBuildings();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const canAccess = ['platform_owner', 'super_agent', 'agent_admin'].includes(user?.role);
  if (!canAccess) {
    return <div className="p-8 text-center text-gray-500">Platform access required.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8" data-testid="platform-buildings-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-gray-200">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Building2 className="w-7 h-7 text-indigo-600" /> Global Building Directory
            </h1>
            <p className="text-gray-600 text-sm">All buildings on the platform · {buildings.length} shown</p>
          </div>
        </div>

        <Card className="mb-4">
          <CardContent className="p-4 flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, address, city..."
                className="pl-9"
                data-testid="buildings-search"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full lg:w-44" data-testid="type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="Sectional Title">Sectional Title</SelectItem>
                <SelectItem value="Homeowners Association">HOA</SelectItem>
                <SelectItem value="Residential Estate">Residential Estate</SelectItem>
                <SelectItem value="Apartment Block">Apartment Block</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              placeholder="Country"
              className="w-full lg:w-32"
              data-testid="country-filter"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full lg:w-36" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="deleted">Deleted</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchBuildings}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
            <Button variant="outline" onClick={exportEmails} data-testid="export-emails-btn"><Download className="w-4 h-4 mr-1" /> Export Emails</Button>
            <Button variant="outline" onClick={recomputeHealth} data-testid="recompute-health-btn"><Activity className="w-4 h-4 mr-1" /> Health</Button>
            {user?.role === 'platform_owner' && (
              <Button onClick={messageSelected} data-testid="message-selected-btn"><Send className="w-4 h-4 mr-1" /> Message</Button>
            )}
          </CardContent>
        </Card>

        {selected.size > 0 && (
          <div className="mb-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm flex flex-wrap items-center gap-2" data-testid="selected-strip">
            <span>{selected.size} building(s) selected · actions apply to selection only</span>
            {user?.role === 'platform_owner' && (
              <>
                <Button size="sm" variant="outline" className="ml-2 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => openStatusDialog('suspend', Array.from(selected))} data-testid="bulk-suspend-btn">
                  <PauseCircle className="w-4 h-4 mr-1" /> Suspend
                </Button>
                <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => openStatusDialog('unsuspend', Array.from(selected))} data-testid="bulk-activate-btn">
                  <PlayCircle className="w-4 h-4 mr-1" /> Activate
                </Button>
                <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => openStatusDialog('archive', Array.from(selected))} data-testid="bulk-delete-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setSelected(new Set())}>Clear</Button>
          </div>
        )}

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={selected.size > 0 && selected.size === buildings.length}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-buildings"
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Units</TableHead>
                  <TableHead className="text-right">Users</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Health</TableHead>
                  <TableHead className="text-right">Open</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8">Loading…</TableCell></TableRow>
                ) : buildings.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-500">No buildings found.</TableCell></TableRow>
                ) : buildings.map((b) => (
                  <TableRow key={b.hoa_id} data-testid={`building-row-${b.hoa_id}`}>
                    <TableCell>
                      <Checkbox checked={selected.has(b.hoa_id)} onCheckedChange={() => toggleOne(b.hoa_id)} data-testid={`select-${b.hoa_id}`} />
                    </TableCell>
                    <TableCell className="font-medium">{b.name || '—'}</TableCell>
                    <TableCell className="text-sm">{b.building_type || '—'}</TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {[b.city, b.province, b.country].filter(Boolean).join(', ') || '—'}
                    </TableCell>
                    <TableCell className="text-right text-sm">{b.total_units || 0}</TableCell>
                    <TableCell className="text-right text-sm">{b.user_count}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${STATUS_COLOURS[b.is_trial ? 'trial' : b.status] || ''} capitalize`}>
                        {b.is_trial ? 'Trial' : (b.status || 'active')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {b.health_score != null ? (
                        <Badge variant="outline" className={HEALTH_COLOURS[b.health_grade] || ''}>
                          {b.health_score}
                        </Badge>
                      ) : <span className="text-gray-400 text-xs">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/platform/buildings/${b.hoa_id}`)} data-testid={`open-${b.hoa_id}`}>
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                        {user?.role === 'platform_owner' && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" data-testid={`row-actions-${b.hoa_id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {b.status !== 'suspended' && (
                                <DropdownMenuItem onClick={() => openStatusDialog('suspend', [b.hoa_id])} data-testid={`suspend-${b.hoa_id}`}>
                                  <PauseCircle className="w-4 h-4 mr-2 text-amber-600" /> Suspend
                                </DropdownMenuItem>
                              )}
                              {(b.status === 'suspended' || b.status === 'deleted') && (
                                <DropdownMenuItem onClick={() => openStatusDialog('unsuspend', [b.hoa_id])} data-testid={`activate-${b.hoa_id}`}>
                                  <PlayCircle className="w-4 h-4 mr-2 text-emerald-600" /> Activate
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openStatusDialog('archive', [b.hoa_id])} data-testid={`soft-delete-${b.hoa_id}`}>
                                <Trash2 className="w-4 h-4 mr-2 text-rose-600" /> Delete (soft)
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-rose-700 focus:bg-rose-50"
                                onClick={() => { setPurgeConfirm(''); setPurgeReason(''); setPurgeDialog({ hoa_id: b.hoa_id, name: b.name || b.hoa_id }); }}
                                data-testid={`purge-${b.hoa_id}`}
                              >
                                <AlertTriangle className="w-4 h-4 mr-2" /> Permanently purge…
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Suspend / Activate / Soft-delete confirm dialog */}
      <Dialog open={!!statusDialog} onOpenChange={(open) => !open && setStatusDialog(null)}>
        <DialogContent className="max-w-md" data-testid="status-dialog">
          <DialogHeader>
            <DialogTitle>
              {statusDialog?.action === 'suspend' && 'Suspend buildings?'}
              {statusDialog?.action === 'unsuspend' && 'Reactivate buildings?'}
              {statusDialog?.action === 'archive' && 'Delete (soft) buildings?'}
            </DialogTitle>
            <DialogDescription>
              {statusDialog?.action === 'suspend' && 'Users keep their accounts but the building is hidden from new logins until reactivated. This is reversible.'}
              {statusDialog?.action === 'unsuspend' && 'Restore access for users in these buildings.'}
              {statusDialog?.action === 'archive' && 'Marks the building as deleted. Data is retained for audit and can be restored with Activate. To remove data permanently use the "Permanently purge" action on a row.'}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <div className="font-medium">Targets ({statusDialog?.hoaIds.length}):</div>
            <ul className="max-h-32 overflow-y-auto text-slate-600 list-disc pl-5">
              {statusDialog?.names.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
            <div>
              <label className="text-sm font-medium">Reason (optional, logged in audit trail)</label>
              <Textarea rows={3} value={statusReason} onChange={(e) => setStatusReason(e.target.value)} data-testid="status-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(null)}>Cancel</Button>
            <Button
              onClick={runStatusAction}
              disabled={busy}
              variant={statusDialog?.action === 'archive' ? 'destructive' : 'default'}
              data-testid="confirm-status-action-btn"
            >
              {busy ? 'Working…' : (statusDialog?.action === 'suspend' ? 'Suspend' : statusDialog?.action === 'unsuspend' ? 'Activate' : 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hard purge dialog */}
      <Dialog open={!!purgeDialog} onOpenChange={(open) => !open && setPurgeDialog(null)}>
        <DialogContent className="max-w-md" data-testid="purge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5" /> Permanently purge building</DialogTitle>
            <DialogDescription>
              This irreversibly deletes the building and all its tenant data (notices, documents, tickets, bookings, votes, agent requests, communications). User accounts are preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <div className="p-2 rounded bg-rose-50 border border-rose-200 text-rose-800">
              Type <span className="font-mono font-semibold">{purgeDialog?.name}</span> to confirm:
            </div>
            <Input value={purgeConfirm} onChange={(e) => setPurgeConfirm(e.target.value)} placeholder={purgeDialog?.name} data-testid="purge-confirm-input" />
            <Textarea rows={2} placeholder="Reason (logged in audit trail)" value={purgeReason} onChange={(e) => setPurgeReason(e.target.value)} data-testid="purge-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeDialog(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={runPurge}
              disabled={busy || (purgeConfirm.trim() !== (purgeDialog?.name || '').trim() && purgeConfirm.trim() !== purgeDialog?.hoa_id)}
              data-testid="confirm-purge-btn"
            >
              {busy ? 'Purging…' : 'Permanently purge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
